import Head from 'next/head';
import Script from 'next/script';
import { useEffect } from 'react';
import { track } from '../../../utils/funnelTracking';
import { VIEW_KEYS, EVENT_TYPES } from '../../../utils/funnelConstants';
import {
  breadcrumbSchema,
  jsonLd,
  medicalWebPageSchema,
  serviceSchema,
  SITE_URL,
} from '../../../utils/schema';
import Breadcrumbs from '../../../components/common/Breadcrumbs';

import Hero from '../../../components/Homepage/hero';

import { LoadService, LoadSpecificServiceBySlug } from '../../../DB/DB';
import ServiceBody from '../../../components/common/ServiceBody';
import HeroMedications from '../../../components/medications/HeroMedications';
import MedicationBody from '../../../components/medications/MedicationBody';

function ServicePage({ data, slug }) {
  const faqs = data?.content?.faqs;
  const category = data?.category?.objectId;

  // Funnel baseline: the condition step. First point where a service is
  // known, so serviceId/serviceName ride along from here to checkout.
  useEffect(() => {
    if (!data?.objectId) return;
    track(VIEW_KEYS.CONDITION, EVENT_TYPES.VIEWED, {
      serviceId: data.objectId,
      serviceName: data.title ?? null,
    });
  }, [data?.objectId, data?.title]);


  // The CMS schema_markup / schema_markup_2 fields are deliberately no longer
  // rendered: editors pasted blocks that duplicated the markup below. Schema
  // for this page is built from the service record instead.
  const pageUrl = `${SITE_URL}/${data?.category?.slug}/${slug}`;
  // Two-level trail — the /[category] level has no page of its own and 404s.
  const trail = [
    { name: 'Home', url: `${SITE_URL}/` },
    { name: data?.title || data?.content?.heading },
  ];

  return (
    <div className='containerLandingPage second2'>
      <Head>
        <title>{data?.meta_title}</title>
        {data?.no_index && (
          <meta name='robots' content='noindex,nofollow' />
        )}
        <meta
          key={'description'}
          name='description'
          content={data?.meta_description}
        />
        <meta property='og:title' content={data?.content?.heading} />
        <meta property='og:type' content='website' />
        <meta
          property='og:url'
          content={`https://www.24hrdoc.com/${data?.category?.slug}/${slug}`}
        />
        <meta property='og:image' content={data?.image?.src} />
        <link
          rel='canonical'
          href={`https://www.24hrdoc.com/${data?.category?.slug}/${slug}`}
        />
        <script
          type='application/ld+json'
          key='webpage-jsonld'
          dangerouslySetInnerHTML={jsonLd(
            medicalWebPageSchema({
              name: data?.meta_title || data?.content?.heading,
              url: pageUrl,
              description: data?.meta_description,
              breadcrumbId: `${pageUrl}#breadcrumb`,
            })
          )}
        />
        <script
          type='application/ld+json'
          key='service-jsonld'
          dangerouslySetInnerHTML={jsonLd(
            serviceSchema({
              name: data?.title || data?.content?.heading,
              description: data?.meta_description || data?.content?.overview,
              url: pageUrl,
              price: data?.Price,
              image: data?.image?.src,
            })
          )}
        />
        <script
          type='application/ld+json'
          key='breadcrumb-jsonld'
          dangerouslySetInnerHTML={jsonLd(breadcrumbSchema(trail, pageUrl))}
        />
        {faqs !== undefined && faqs.length > 0 && (
          <script
            type='application/ld+json'
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',

                mainEntity: faqs?.map((faq) => ({
                  '@type': 'Question',
                  name: faq?.title,
                  acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq?.content,
                  },
                })),
              }),
            }}
          ></script>
        )}
      </Head>

      <Script
        type='text/javascript'
        src='//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
        strategy='lazyOnload'
      />

      {/* The Hero Component. Breadcrumbs render inside it so they line up
          with the H1 and share the hero's container width. */}
      {category === '1LHzR8eHNc' ? (
        <HeroMedications
          breadcrumbs={<Breadcrumbs trail={trail} />}
          category={data?.category}
          title={data?.content?.heading}
          subText={data?.content?.overview}
          avatar={data?.thumbnail}
          hero={data?.image}
          path={
            data?.category?.objectId == 'iezHwCrWYY'
              ? `/diagnosis/weight-loss/?diseaseID=${data?.objectId}`
              : `/diagnosis?diseaseID=${data?.objectId}`
          }
          sections={data?.content?.sections}
          price={data?.Price}
          content={data?.content}
          service
          how_it_works={data?.how_it_works}
          right_for_you={data?.right_for_you}
        />
      ) : (
        <Hero
          breadcrumbs={<Breadcrumbs trail={trail} />}
          category={data?.category}
          title={data?.content?.heading}
          subText={data?.content?.overview}
          avatar={data?.thumbnail}
          hero={data?.image}
          path={
            data?.category?.objectId == 'iezHwCrWYY'
              ? `/diagnosis/weight-loss/?diseaseID=${data?.objectId}`
              : `/diagnosis?diseaseID=${data?.objectId}`
          }
          sections={data?.content?.sections}
          price={data?.Price}
          heading={data?.heading}
          showNav={data?.content?.sections?.length == 0 ? false : true}
          service
          image2={data?.image_mid?.src}
          how_it_works={data?.how_it_works ? data?.how_it_works : null}
          right_for_you={data?.right_for_you}
          objectID={data?.objectId}
        />
      )}

      {/* Another component for disease details */}
      {category === '1LHzR8eHNc' ? (
        <MedicationBody
          data={data}
          title={data?.content?.heading}
          path={
            data?.category?.objectId == 'iezHwCrWYY'
              ? `/diagnosis/weight-loss/?diseaseID=${data?.objectId}`
              : `/diagnosis?diseaseID=${data?.objectId}`
          }
        />
      ) : (
        <ServiceBody data={data} faqs={faqs} />
      )}
    </div>
  );
}

export default ServicePage;

export async function getStaticPaths() {
  const { results } = await LoadService('objectId,title,slug');

  let paths = results
    .filter((service) => service.category.slug == 'LN5pjN3x6p')
    .map((service) => {
      return {
        params: {
          slug: service.slug, // ✅ Include title
          category_slug: service.category.slug, // ✅ Include title
        },
      };
    });

  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }) {
  const category_slug = params.category_slug; // ✅ Extract from the array
  const slug = params.slug; // ✅ Extract from the array

  const { results } = await LoadSpecificServiceBySlug(
    slug,
    'title,Price,description,content,meta_title,meta_description,canonical_url,image,image_description,thumbnail,has_content,slug,category.slug,video_main,symptoms,quick_facts,image_mid,schema_markup,schema_markup_2,how_it_works,right_for_you,no_index'
  );

  if (!results[0]) {
    return { notFound: true };
  }

  return {
    props: {
      data: results[0],
      slug: slug,
    },
    revalidate: 3600,
  };
}

function getTitle(str) {
  return str.toLowerCase().replace(/[\s+#]/g, '-');
}
